"use client";

import type { IconValue } from "@gettactic/components/src/icons";
import { useWorkplaceRequestsAllowed } from "@gettactic/hooks";
import { useMemo } from "react";
import { parseIconValue } from "@/features/settings/components/WorkplaceRequestsPage/requests/icon-value";
import { useWorkplaceRequestCategories } from "./useWorkplaceRequestCategories";
import { useWorkplaceRequestTypes } from "./useWorkplaceRequestTypes";

export interface WorkplaceRequestTypeVisual {
  name: string;
  /** Icon of the request type, already parsed from its `set:icon` value. */
  icon: IconValue | null;
  /** Background tint of the icon tile — the owning category's color. */
  color: string | null;
}

/**
 * Maps request type id -> the visuals used to render it (icon + owning
 * category color). Use it wherever a request is shown next to other entities
 * and only its type id is available, e.g. on the timeline event cards.
 */
export const useWorkplaceRequestTypeVisuals = (
  options: { enabled?: boolean } = {}
): Map<string, WorkplaceRequestTypeVisual> => {
  const isWorkplaceRequestsAllowed =
    useWorkplaceRequestsAllowed("workplace_requests");
  const enabled = (options.enabled ?? true) && isWorkplaceRequestsAllowed;

  const types = useWorkplaceRequestTypes({}, { enabled });
  const categories = useWorkplaceRequestCategories({}, { enabled });

  return useMemo(() => {
    const colorByCategoryId = new Map(
      categories.allElements.map((category) => [category.id, category.color])
    );
    return new Map(
      types.allElements.map((type) => [
        type.id,
        {
          name: type.name,
          icon: parseIconValue(type.icon) ?? null,
          color: colorByCategoryId.get(type.category_id) ?? null
        }
      ])
    );
  }, [types.allElements, categories.allElements]);
};
